import React from 'react';
import { KatButtonGroup, KatButton } from '@amzn/katal-react';
import { useTranslation } from 'react-i18next';
import { SortOptions, FilterOptions } from 'src/types/DiscussionPost';
import {
  selectFilter,
  selectSort,
  threadDetailActions,
  selectThreadDetailOp,
  selectReplyCount,
} from 'src/redux/slices/threadDetailSlice';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { useRouter } from 'next/router';
import { getThreadDetail } from 'src/constants/pages';
import { AppState } from 'src/redux/store';
import { ProfileStatus } from 'src/types/UserInfo';
import { useMetrics } from 'src/utils';
import { ButtonTypes } from 'src/constants/metrics';

/**
 * @todo create the filters as a reusable component
 * sim - https://sim.amazon.com/issues/SPCE-2984
 */
const ReplyFilter: React.FC = () => {
  const { t, i18n: { format, language } } = useTranslation();
  const { clickMetric } = useMetrics();
  const { push } = useRouter();
  const dispatch = useAppDispatch();
  const filterBy = useAppSelector(selectFilter);
  const sortBy = useAppSelector(selectSort);
  const opPost = useAppSelector(selectThreadDetailOp);
  const replyCount = useAppSelector(selectReplyCount);
  const { profileStatus } = useAppSelector(({ userInfo }: AppState) => userInfo);
  const resetQueryParams = () => {
    push(
      {
        pathname: getThreadDetail(opPost.threadId),
        query: { },
      },
      undefined,
      {
        shallow: true,
      },
    );
  };
  const updateFilter = (value: FilterOptions) => {
    resetQueryParams();
    clickMetric({
      metricValue: value,
      metricName: ButtonTypes.repliesFilter,
    });
    dispatch(threadDetailActions.setFilterBy(value));
  };

  const updateSort = (value: SortOptions) => {
    resetQueryParams();
    clickMetric({
      metricValue: value,
      metricName: ButtonTypes.repliesFilter,
    });
    dispatch(threadDetailActions.setSortBy(value));
  };

  const filterOptions = [
    { label: t('filters_allReplies'), value: FilterOptions.allReplies },
    { label: t('filters_amazonReplies'), value: FilterOptions.amazonReplies },
  ];
  if (profileStatus === ProfileStatus.registered) {
    filterOptions.splice(1, 0, { label: t('filters_myReplies'), value: FilterOptions.myReplies });
  }
  return (
    <div className="container my-4 flex flex-row justify-between">
      <div className="flex flex-row gap-x-5 ">
        <div className="text-lg font-semibold">
          {t('numberOfReplies', { replyCount, formattedCount: format(replyCount, 'compactNumber', language) })}
        </div>

        <div className="mt-1">
          <div className="text-sm font-semibold">{t('filters_header')}</div>
          {
          filterOptions.map(({ label, value }) => {
            const isSelected = !!(filterBy === value);
            const style = isSelected
              ? 'bg-kat-eastern text-white border-kat-eastern'
              : 'bg-kat-snow text-kat-nordic border-kat-seattle';
            return (
              <label htmlFor={value} key={value} data-testid="tag-option" className="mt-2 ltr:mr-2 rtl:ml-2">
                <button
                  id={value}
                  type="button"
                  className={`${style} cursor-pointer rounded-full border border-solid py-1 px-5`}
                  onClick={() => updateFilter(value)}
                >
                  {label}
                </button>
                <div />
              </label>
            );
          })
        }
        </div>
      </div>
      <div>
        <div className="mb-1 text-sm font-medium">{t('sort_header')}</div>
        <KatButtonGroup
          onChange={({ detail: { value } }) => updateSort(value as SortOptions)}
          value={sortBy}
        >
          <KatButton label={t('sort_oldest')} value={SortOptions.oldest} />
          <KatButton label={t('sort_newest')} value={SortOptions.newest} />
          <KatButton label={t('sort_mostVoted')} value={SortOptions.mostVotes} />
        </KatButtonGroup>
      </div>
    </div>
  );
};

export default ReplyFilter;
